import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../../lib/supabase';

const CURRENT_PERIOD = (() => {
  const now = new Date();
  return `${now?.getFullYear()}-${String(now?.getMonth() + 1)?.padStart(2, '0')}`;
})();

const PencilIcon = () => (
  <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
    <path d="M9.5 2.5L11.5 4.5L4.5 11.5H2.5V9.5L9.5 2.5Z" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M8 4L10 6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
  </svg>
);

const TrashIcon = () => (
  <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
    <path d="M2 4h10M5 4V2.5h4V4M5.5 6.5v4M8.5 6.5v4M3 4l.8 7.5h6.4L11 4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const SkeletonCard = () => (
  <div className="rounded-xl border p-5 animate-pulse" style={{ backgroundColor: '#111111', borderColor: '#1f1f1f' }}>
    <div className="h-4 rounded w-1/3 mb-3" style={{ backgroundColor: '#1f1f1f' }} />
    <div className="h-6 rounded w-2/3 mb-4" style={{ backgroundColor: '#1f1f1f' }} />
    <div className="h-3 rounded w-1/2" style={{ backgroundColor: '#1f1f1f' }} />
  </div>
);

const ServersSection = ({ servers, onServersChange, showToast }) => {
  const { t } = useTranslation();
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingServer, setEditingServer] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDeleteServer, setConfirmDeleteServer] = useState(null);

  // Add modal state
  const [newId, setNewId] = useState('');
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState('#3b82f6');
  const [copyFrom, setCopyFrom] = useState('');
  const [formError, setFormError] = useState('');

  // Edit modal state
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState('');

  const resetAddForm = () => {
    setNewId('');
    setNewName('');
    setNewColor('#3b82f6');
    setCopyFrom('');
    setFormError('');
  };

  const handleToggleActive = async (server) => {
    const { error } = await supabase?.from('servers')?.update({ is_active: !server?.is_active })?.eq('id', server?.id);
    if (error) {
      showToast(t('admin.config.errorUpdate'), 'error');
    } else {
      showToast(!server?.is_active ? t('admin.config.serverActivated', { id: server?.id }) : t('admin.config.serverDeactivated', { id: server?.id }), 'success');
      onServersChange();
    }
  };

  const handleAddServer = async () => {
    const id = newId?.trim()?.toUpperCase();
    const name = newName?.trim();
    if (!id || !name) {
      setFormError(t('admin.config.idAndNameRequired'));
      return;
    }
    if (servers?.find(s => s?.id === id)) {
      setFormError(t('admin.config.serverIdExists'));
      return;
    }
    setSaving(true);
    setFormError('');

    // 1. Insert server
    const { error: serverError } = await supabase?.from('servers')?.insert({ id, name, color: newColor, is_active: true });

    if (serverError) {
      setFormError(serverError?.message);
      setSaving(false);
      return;
    }

    // 2. Copy config if selected
    if (copyFrom) {
      // Copy rankup_config
      const { data: rankupRows } = await supabase?.from('rankup_config')?.select('*')?.eq('server', copyFrom);

      if (rankupRows && rankupRows?.length > 0) {
        const newRankupRows = rankupRows?.map(r => ({
          server: id,
          server_id: id,
          tier: r?.tier,
          min_diamonds: r?.min_diamonds,
          ratio_rankup: r?.ratio_rankup,
          ratio_maintain: r?.ratio_maintain,
          min_live_days: r?.min_live_days,
          min_live_hours: r?.min_live_hours,
          valid_from: new Date()?.toISOString()?.split('T')?.[0],
        }));
        await supabase?.from('rankup_config')?.insert(newRankupRows);
      }

      // Copy activeness_config
      const { data: activenessRows } = await supabase?.from('activeness_config')?.select('*')?.eq('server', copyFrom);

      if (activenessRows && activenessRows?.length > 0) {
        const newActivenessRows = activenessRows?.map(r => ({
          server: id,
          server_id: id,
          level: r?.level,
          min_diamonds: r?.min_diamonds,
          min_live_days: r?.min_live_days,
          min_live_hours: r?.min_live_hours,
          bonus_ratio: r?.bonus_ratio,
          valid_from: new Date()?.toISOString()?.split('T')?.[0],
        }));
        await supabase?.from('activeness_config')?.insert(newActivenessRows);
      }

      // Copy incremental_config
      const { data: incrRows } = await supabase?.from('incremental_config')?.select('*')?.eq('server', copyFrom);

      if (incrRows && incrRows?.length > 0) {
        const newIncrRows = incrRows?.map(r => ({
          server: id,
          server_id: id,
          period: CURRENT_PERIOD,
          target_100pct: r?.target_100pct,
          completion_tiers: r?.completion_tiers,
        }));
        await supabase?.from('incremental_config')?.insert(newIncrRows);
      } else {
        // Insert blank incremental_config
        await supabase?.from('incremental_config')?.insert({
          server: id,
          server_id: id,
          period: CURRENT_PERIOD,
          target_100pct: 0,
          completion_tiers: [],
        });
      }
    } else {
      // Insert blank incremental_config
      await supabase?.from('incremental_config')?.insert({
        server: id,
        server_id: id,
        period: CURRENT_PERIOD,
        target_100pct: 0,
        completion_tiers: [],
      });
    }

    setSaving(false);
    setShowAddModal(false);
    resetAddForm();
    showToast(t('admin.config.serverCreated', { id }), 'success');
    onServersChange();
  };

  const handleEditOpen = (server) => {
    setEditingServer(server);
    setEditName(server?.name);
    setEditColor(server?.color || '#888888');
  };

  const handleEditSave = async () => {
    if (!editName?.trim()) return;
    setSaving(true);
    const { error } = await supabase?.from('servers')?.update({ name: editName?.trim(), color: editColor })?.eq('id', editingServer?.id);
    setSaving(false);
    if (error) {
      showToast(error?.message, 'error');
    } else {
      showToast(t('admin.config.serverUpdated'), 'success');
      setEditingServer(null);
      onServersChange();
    }
  };

  const handleDelete = async (server) => {
    // Check if any creators are linked
    const { count } = await supabase?.from('creators')?.select('id', { count: 'exact', head: true })?.eq('server', server?.id);

    if (count > 0) {
      showToast(t('admin.config.cannotDeleteLinked', { count }), 'error');
      return;
    }

    setConfirmDeleteServer(server);
  };

  const handleConfirmDelete = async () => {
    const server = confirmDeleteServer;
    setConfirmDeleteServer(null);
    setDeletingId(server?.id);

    // Step 1: Delete all related config rows
    await supabase?.from('rankup_config')?.delete()?.eq('server_id', server?.id);
    await supabase?.from('activeness_config')?.delete()?.eq('server_id', server?.id);
    await supabase?.from('incremental_config')?.delete()?.eq('server_id', server?.id);

    // Step 2: Delete the server
    const { error } = await supabase?.from('servers')?.delete()?.eq('id', server?.id);
    setDeletingId(null);
    if (error) {
      showToast(error?.message, 'error');
    } else {
      showToast(t('admin.config.serverDeleted', { id: server?.id }), 'success');
      onServersChange();
    }
  };

  return (
    <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: '#111111', borderColor: '#1f1f1f' }}>
      {/* Header */}
      <div className="px-6 py-4 border-b flex items-center justify-between" style={{ borderColor: '#1f1f1f' }}>
        <div>
          <h2 className="text-white font-semibold text-base" style={{ fontFamily: 'Sora, sans-serif' }}>
            {t('admin.config.serversTitle')}
          </h2>
          <p className="text-white/40 text-xs mt-0.5" style={{ fontFamily: 'Inter, sans-serif' }}>
            {t('admin.config.serversSubtitle')}
          </p>
        </div>
        <button
          onClick={() => { resetAddForm(); setShowAddModal(true); }}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold transition-all hover:opacity-80"
          style={{ backgroundColor: '#E5202A22', color: '#E5202A', border: '1px solid #E5202A40', fontFamily: 'Sora, sans-serif' }}
        >
          <span>+</span> {t('admin.config.addServer')}
        </button>
      </div>
      {/* Cards Grid */}
      <div className="p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {servers?.length === 0 ? (
          [1, 2, 3, 4]?.map(i => <SkeletonCard key={i} />)
        ) : (
          servers?.map(server => {
            const color = server?.color || '#888888';
            return (
              <div
                key={server?.id}
                className="rounded-xl border p-5 flex flex-col gap-3"
                style={{ backgroundColor: '#0d0d0d', borderColor: '#1f1f1f', borderLeft: `3px solid ${color}` }}
              >
                {/* ID Badge */}
                <div className="flex items-center justify-between">
                  <span
                    className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-bold"
                    style={{ backgroundColor: `${color}22`, color, fontFamily: 'Sora, sans-serif' }}
                  >
                    {server?.id}
                  </span>
                  {/* Active Toggle */}
                  <button
                    onClick={() => handleToggleActive(server)}
                    className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold transition-all"
                    style={{
                      backgroundColor: server?.is_active ? '#22c55e18' : '#ef444418',
                      color: server?.is_active ? '#22c55e' : '#ef4444',
                      border: `1px solid ${server?.is_active ? '#22c55e30' : '#ef444430'}`,
                      fontFamily: 'Inter, sans-serif',
                    }}
                  >
                    <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: 'currentColor', display: 'inline-block' }} />
                    {server?.is_active ? t('admin.config.activeStatus') : t('admin.config.inactiveStatus')}
                  </button>
                </div>
                {/* Name */}
                <div>
                  <p className="text-white font-semibold text-sm" style={{ fontFamily: 'Sora, sans-serif' }}>
                    {server?.name}
                  </p>
                </div>
                {/* Color */}
                <div className="flex items-center gap-2">
                  <span className="text-white/40 text-xs" style={{ fontFamily: 'Inter, sans-serif' }}>{t('admin.config.colorLabel')}</span>
                  <div
                    className="w-5 h-5 rounded-full border border-white/10"
                    style={{ backgroundColor: color }}
                  />
                  <span className="text-white/50 text-xs font-mono">{color}</span>
                </div>
                {/* Actions */}
                <div className="flex gap-2 mt-1">
                  <button
                    onClick={() => handleEditOpen(server)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all hover:opacity-80 flex-1 justify-center"
                    style={{ backgroundColor: `${color}18`, color, border: `1px solid ${color}30`, fontFamily: 'Inter, sans-serif' }}
                  >
                    <PencilIcon /> {t('admin.config.edit')}
                  </button>
                  <button
                    onClick={() => handleDelete(server)}
                    disabled={deletingId === server?.id}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all hover:opacity-80"
                    style={{ backgroundColor: '#ef444418', color: '#ef4444', border: '1px solid #ef444430', fontFamily: 'Inter, sans-serif', opacity: deletingId === server?.id ? 0.5 : 1 }}
                  >
                    <TrashIcon />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
      {/* ADD MODAL */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}>
          <div className="rounded-2xl border p-6 w-full max-w-md mx-4" style={{ backgroundColor: '#111111', borderColor: '#1f1f1f' }}>
            <h3 className="text-white font-bold text-base mb-5" style={{ fontFamily: 'Sora, sans-serif' }}>
              {t('admin.config.addServerTitle')}
            </h3>
            <div className="flex flex-col gap-4">
              <div>
                <label className="text-white/50 text-xs mb-1.5 block" style={{ fontFamily: 'Inter, sans-serif' }}>{t('admin.config.labelId')}</label>
                <input
                  value={newId}
                  onChange={e => setNewId(e?.target?.value?.toUpperCase())}
                  placeholder="US"
                  className="w-full rounded-lg px-3 py-2 text-base text-white outline-none border"
                  style={{ backgroundColor: '#0d0d0d', borderColor: '#2a2a2a', fontFamily: 'Sora, sans-serif' }}
                />
              </div>
              <div>
                <label className="text-white/50 text-xs mb-1.5 block" style={{ fontFamily: 'Inter, sans-serif' }}>{t('admin.config.labelName')}</label>
                <input
                  value={newName}
                  onChange={e => setNewName(e?.target?.value)}
                  placeholder="United States"
                  className="w-full rounded-lg px-3 py-2 text-base text-white outline-none border"
                  style={{ backgroundColor: '#0d0d0d', borderColor: '#2a2a2a', fontFamily: 'Inter, sans-serif' }}
                />
              </div>
              <div>
                <label className="text-white/50 text-xs mb-1.5 block" style={{ fontFamily: 'Inter, sans-serif' }}>{t('admin.config.labelColor')}</label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={newColor}
                    onChange={e => setNewColor(e?.target?.value)}
                    className="w-10 h-10 rounded-lg cursor-pointer border-0 bg-transparent"
                    style={{ padding: 0 }}
                  />
                  <span className="text-white/50 text-xs font-mono">{newColor}</span>
                </div>
              </div>
              <div>
                <label className="text-white/50 text-xs mb-1.5 block" style={{ fontFamily: 'Inter, sans-serif' }}>{t('admin.config.copyConfigFrom')}</label>
                <select
                  value={copyFrom}
                  onChange={e => setCopyFrom(e?.target?.value)}
                  className="w-full rounded-lg px-3 py-2 text-base text-white outline-none border"
                  style={{ backgroundColor: '#0d0d0d', borderColor: '#2a2a2a', fontFamily: 'Inter, sans-serif' }}
                >
                  <option value="">{t('admin.config.noCopy')}</option>
                  {servers?.map(s => (
                    <option key={s?.id} value={s?.id}>{s?.id} — {s?.name}</option>
                  ))}
                </select>
              </div>
              {formError && (
                <p className="text-xs" style={{ color: '#ef4444', fontFamily: 'Inter, sans-serif' }}>{formError}</p>
              )}
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={handleAddServer}
                disabled={saving}
                className="flex-1 py-2.5 rounded-lg text-sm font-bold transition-all"
                style={{ backgroundColor: '#E5202A22', color: '#E5202A', border: '1px solid #E5202A40', fontFamily: 'Sora, sans-serif', opacity: saving ? 0.6 : 1 }}
              >
                {saving ? t('admin.config.creating') : t('admin.config.createServer')}
              </button>
              <button
                onClick={() => { setShowAddModal(false); resetAddForm(); }}
                disabled={saving}
                className="px-4 py-2.5 rounded-lg text-sm font-semibold"
                style={{ backgroundColor: '#1f1f1f', color: 'rgba(255,255,255,0.5)', fontFamily: 'Inter, sans-serif' }}
              >
                {t('admin.config.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* EDIT MODAL */}
      {editingServer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}>
          <div className="rounded-2xl border p-6 w-full max-w-sm mx-4" style={{ backgroundColor: '#111111', borderColor: '#1f1f1f' }}>
            <h3 className="text-white font-bold text-base mb-5" style={{ fontFamily: 'Sora, sans-serif' }}>
              {t('admin.config.editServer', { id: editingServer?.id })}
            </h3>
            <div className="flex flex-col gap-4">
              <div>
                <label className="text-white/50 text-xs mb-1.5 block" style={{ fontFamily: 'Inter, sans-serif' }}>{t('admin.config.labelName')}</label>
                <input
                  value={editName}
                  onChange={e => setEditName(e?.target?.value)}
                  className="w-full rounded-lg px-3 py-2 text-base text-white outline-none border"
                  style={{ backgroundColor: '#0d0d0d', borderColor: '#2a2a2a', fontFamily: 'Inter, sans-serif' }}
                />
              </div>
              <div>
                <label className="text-white/50 text-xs mb-1.5 block" style={{ fontFamily: 'Inter, sans-serif' }}>{t('admin.config.labelColor')}</label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={editColor}
                    onChange={e => setEditColor(e?.target?.value)}
                    className="w-10 h-10 rounded-lg cursor-pointer border-0 bg-transparent"
                    style={{ padding: 0 }}
                  />
                  <span className="text-white/50 text-xs font-mono">{editColor}</span>
                </div>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={handleEditSave}
                disabled={saving}
                className="flex-1 py-2.5 rounded-lg text-sm font-bold transition-all"
                style={{ backgroundColor: '#22c55e22', color: '#22c55e', border: '1px solid #22c55e40', fontFamily: 'Sora, sans-serif', opacity: saving ? 0.6 : 1 }}
              >
                {saving ? t('admin.config.savingLabel') : t('admin.config.saveLabel')}
              </button>
              <button
                onClick={() => setEditingServer(null)}
                className="px-4 py-2.5 rounded-lg text-sm font-semibold"
                style={{ backgroundColor: '#1f1f1f', color: 'rgba(255,255,255,0.5)', fontFamily: 'Inter, sans-serif' }}
              >
                {t('admin.config.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* DELETE CONFIRM MODAL */}
      {confirmDeleteServer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}>
          <div className="rounded-2xl border p-6 w-full max-w-sm mx-4" style={{ backgroundColor: '#111111', borderColor: '#1f1f1f' }}>
            <h3 className="text-white font-bold text-base mb-3" style={{ fontFamily: 'Sora, sans-serif' }}>
              {t('admin.config.deleteServerTitle', { id: confirmDeleteServer?.id })}
            </h3>
            <p className="text-white/50 text-sm mb-6" style={{ fontFamily: 'Inter, sans-serif' }}>
              {t('admin.config.deleteServerBody')}
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleConfirmDelete}
                className="flex-1 py-2.5 rounded-lg text-sm font-bold transition-all"
                style={{ backgroundColor: '#ef444422', color: '#ef4444', border: '1px solid #ef444440', fontFamily: 'Sora, sans-serif' }}
              >
                {t('admin.config.confirm')}
              </button>
              <button
                onClick={() => setConfirmDeleteServer(null)}
                className="px-4 py-2.5 rounded-lg text-sm font-semibold"
                style={{ backgroundColor: '#1f1f1f', color: 'rgba(255,255,255,0.5)', fontFamily: 'Inter, sans-serif' }}
              >
                {t('admin.config.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServersSection;
